import { useEffect, useState } from "react";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

// Carte « Double vérification (2FA) » de l'onglet Paramètres (demande du propriétaire) :
// activation par QR (Google/Microsoft Authenticator) + code fixe de secours (serveur).
// AVIS DE 30 JOURS : obligatoire pour les comptes administrateurs à compter du
// 22 octobre 2026 — le compte à rebours est affiché tant qu'elle n'est pas active.
export const TwoFactorCard = () => {
  const [st, setSt] = useState(null);        // {enabled, required_after, grace_days_left}
  const [setup, setSetup] = useState(null);  // {qr, secret} — affiché une seule fois
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/auth/2fa/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const begin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
      setCode("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const activate = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/activate", { code });
      toast.success("Double vérification ACTIVÉE — un code sera demandé à chaque connexion.");
      setSetup(null);
      setCode("");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const disable = async () => {
    if (!code.trim()) { toast.error("Entrez un code de l'app (ou le code de secours) pour désactiver."); return; }
    if (!window.confirm("Désactiver la double vérification de votre compte administrateur ?")) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("Double vérification désactivée.");
      setCode("");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (!st) return null;

  return (
    <div className="bg-surface border border-ivory/10 rounded-xl p-7 mb-8" data-testid="twofa-card">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <ShieldCheck className="w-5 h-5 text-gold" />
        <h3 className="font-semibold">Double vérification (2FA)</h3>
        <span data-testid="twofa-status"
          className={`text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 border ${
            st.enabled ? "border-emerald-400/50 text-emerald-300 bg-emerald-400/10"
                       : "border-amber-400/50 text-amber-300 bg-amber-400/10"}`}>
          {st.enabled ? "Active" : "Inactive"}
        </span>
      </div>
      <p className="text-xs text-ivory/60 mb-4">
        À la connexion, un code à 6 chiffres de votre app d'authentification (Google ou Microsoft
        Authenticator) est exigé en plus du mot de passe. Un code de secours fixe (conservé par
        Mr Trophée) permet d'entrer si le téléphone est perdu.
        {!st.enabled && st.grace_days_left > 0 && (
          <strong className="text-amber-300"> Obligatoire dans {st.grace_days_left} jour{st.grace_days_left > 1 ? "s" : ""}
            {" "}({new Date(st.required_after + "T00:00:00").toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" })}).</strong>
        )}
      </p>

      {!st.enabled && !setup && (
        <button onClick={begin} disabled={busy} data-testid="twofa-begin"
          className="inline-flex items-center gap-2 bg-gold text-ink font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Activer la double vérification
        </button>
      )}

      {setup && (
        <div className="border border-gold/25 rounded-xl p-5 space-y-4" data-testid="twofa-setup">
          <p className="text-sm text-ivory/80">
            1. Scannez ce code avec votre app d'authentification :
          </p>
          <img src={setup.qr} alt="QR code double vérification" data-testid="twofa-qr"
            className="rounded-xl border border-gold/30 bg-white p-3 w-44 h-44" />
          <p className="text-xs text-ivory/60">
            Impossible de scanner ? Saisie manuelle :
            <code data-testid="twofa-secret" className="text-gold tracking-widest select-all ml-2">{setup.secret}</code>
          </p>
          <p className="text-sm text-ivory/80">2. Entrez le code à 6 chiffres affiché dans l'app :</p>
          <div className="flex flex-wrap gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value)} data-testid="twofa-code"
              type="text" inputMode="numeric" autoComplete="one-time-code" placeholder="123 456"
              className="bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm w-44 text-center tracking-[0.3em] focus:border-gold focus:outline-none" />
            <button onClick={activate} disabled={busy || code.trim().length < 6} data-testid="twofa-activate"
              className="bg-gold text-ink font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
              Confirmer l'activation
            </button>
            <button onClick={() => { setSetup(null); setCode(""); }} data-testid="twofa-cancel"
              className="border border-ivory/20 text-ivory/70 rounded-full px-5 py-2.5 text-sm hover:border-gold/50 hover:text-gold transition-colors duration-300">
              Annuler
            </button>
          </div>
        </div>
      )}

      {st.enabled && (
        <div className="flex flex-wrap items-center gap-2" data-testid="twofa-disable-zone">
          <input value={code} onChange={(e) => setCode(e.target.value)} data-testid="twofa-disable-code"
            type="text" inputMode="numeric" autoComplete="one-time-code" placeholder="Code de l'app ou secours"
            className="bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm w-56 focus:border-gold focus:outline-none" />
          <button onClick={disable} disabled={busy} data-testid="twofa-disable"
            className="border border-red-500/40 text-red-400 rounded-full px-5 py-2.5 text-sm hover:bg-red-500 hover:text-ivory transition-colors duration-300 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin inline mr-1" />} Désactiver
          </button>
        </div>
      )}
    </div>
  );
};
